/* eslint-disable @nx/enforce-module-boundaries */
'use-client';

import {
  CircleFilled,
  VerticalThreeDot,
  NewPagesGroupIcon,
  CompanyIndustryIcon,
  JobLocationIcon,
  CompanyIndustryIconNew,
  WebsiteIcon,
  FollowIcon,
  HorizontalThreeDot,
} from '@mybridge/icons';
import { Button } from '@mybridge/ui/button';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOnClickOutside } from 'v4/components/hooks/useOnClickOutside';
import DeactivatePageModal from 'v4/snippets/settings/modals/DeactivatePageModal';

import {
  Avatar,
  Box,
  Card,
  CardBody,
  Circle,
  Flex,
  HStack,
  Heading,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Spacer,
  IconButton,
  Wrap,
  WrapItem,
  Grid,
  useToast,
  Portal,
} from '@mybridge/ui';
import PermissionImg from 'v4/assets/permissions.svg';
import {
  clearCompanyPageDetail,
  deleteCompanyPage,
  getAllCompanyPages,
} from 'v4/store/actions/companyPages.actions';
import Link from 'next/link';
import { AuthenticatedProviders } from 'v4/snippets/providers/authenticated';
import { isMobile } from 'react-device-detect';
import { BackArrow } from '@mybridge/icons';
import Head from 'next/head';
import { CompanyIcon } from '@mybridge/icons/Company';
import { getUserLocation } from 'v4/lib/commons';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { ChevronDownIcon } from '@chakra-ui/icons';

export default function Pages({ setSelectedComponent }) {
  const { companyPageList } = useSelector((state) => state.companyPage);
  const { loggedIn, pageLoaded } = useSelector((state) => state.user);
  const { globalNotifCount } = useSelector((state) => state.notifications);
  const router = useRouter();
  const { t } = useTranslator();
  const toast = useToast();
  const dispatch = useDispatch();

  const [activeMenu, setActive] = useState(null);
  const [modalType, setModalType] = useState('');
  const [company, setCompany] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    dispatch(getAllCompanyPages());
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  const onActiveMenu = (page, index) => {
    // console.log(page, index);
    setActive(index);
    setCompany(page);
  };

  const onClose = (event, type) => {
    setActive(null);
    setModalType('');
    setCompany({});
    if (type === 'triggerDeactivate') {
      setModalType('deactivation');
    }
  };

  const deletePage = (page, actionType) => {
    if (actionType === 'deactivate' || actionType === 'delete') {
      setCompany(page);
      setActive(null);
      setModalType(actionType);
    } else {
      dispatch(
        deleteCompanyPage({
          id: page.id,
          actionType: actionType,
          reason: 'Direct action from menu',
          password: null,
        })
      ).then((res) => {
        if (res.payload.status_code === 403) {
          toast({
            title: 'Error',
            description: res.payload.detail,
            status: 'error',
            duration: 2000,
            isClosable: true,
            position: 'top',
          });
        } else {
          toast({
            title: 'Success',
            description:
              res?.payload?.message || `Page ${actionType}d successfully`,
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top',
          });
          dispatch(getAllCompanyPages());
        }
      });
    }
  };

  const ref = React.useRef();
  useOnClickOutside(ref, () => onClose());

  // console.log('companyPageList', companyPageList);

  return (
    <AuthenticatedProviders>
      <Head>
        <title>
          {loggedIn && globalNotifCount ? `(${globalNotifCount})` : ''}{' '}
          {t('otherModules.settings') || 'Settings'}-{' '}
          {t('profileNetwork.pages') || 'Pages'} | myBridge.me
        </title>
      </Head>
      <Box display="flex" flexDirection="column" h="82vh">
        <HStack
          w="100%"
          p={['10px', '0 0 10px']}
          alignItems="center"
          justifyContent="space-between"
          position="sticky"
        >
          <Menu>
            <MenuButton 
              as={Button} 
              rightIcon={<ChevronDownIcon />}
              variant="outline"
              borderRadius="30px"
              borderColor="gray.300"
              size="sm"
            >
              {activeFilter === 'all' && (t('pages.All Pages') || 'All Pages')}
              {activeFilter === 'active' && (t('pages.Activated Pages') || 'Activated Pages')}
              {activeFilter === 'deactivated' && (t('pages.Deactivated Pages') || 'Deactivated Pages')}
            </MenuButton>
            <Portal>
              <MenuList zIndex={9999}>
                <MenuItem onClick={() => setActiveFilter('all')}>
                  {t('pages.All Pages') || 'All Pages'}
                </MenuItem>
                <MenuItem onClick={() => setActiveFilter('active')}>
                  {t('pages.Activated Pages') || 'Activated Pages'}
                </MenuItem>
                <MenuItem onClick={() => setActiveFilter('deactivated')}>
                  {t('pages.Deactivated Pages') || 'Deactivated Pages'}
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
          <Spacer />
          <Button
            borderRadius="30px"
            p={[0, 2]}
            variant="ghost"
            onClick={() => {
              if (isMobile) {
                setSelectedComponent('create-new-page');
              } else {
                dispatch(clearCompanyPageDetail());
                router.push('/page/create');
              }
            }}
          >
            <Text color="brandGray.700" me="10px">
              {t('pages.createNewPage') || 'Create a new Page'}
            </Text>
            <CircleFilled />
          </Button>
        </HStack>

        <Stack spacing="4" px={['5px', 0]} flex={1} overflowY="auto" 
          style={{
            scrollbarWidth: 'none', /* Firefox */
            msOverflowStyle: 'none', /* Internet Explorer 10+ */
          }}
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none', /* Safari and Chrome */
            }
          }}
        >
          {companyPageList && companyPageList?.length ? (
            <Grid
              // pt={8}
              mb={"50px"}
              templateColumns={isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)'}
              gap={4}
            >
              {companyPageList
                  ?.filter(page => {
                    if (activeFilter === 'all') return true;
                    if (activeFilter === 'active') return !page.is_deactivated;
                    if (activeFilter === 'deactivated') return page.is_deactivated;
                    return true;
                  })
                  ?.map((page, index) => {

                    return (
                      <Card
                        key={index}
                        p={[2, 2]}
                        variant="thinBorderedCard"
                        //boxShadow="none"
                        boxShadow="md"
                        minH={["250px", "250px", "250px","268px"]}
                        w="100%"
                        border="1px solid #D4D4D4"
                        borderRadius="20px"
                      >
                        <HStack alignItems="flex-start">
                          {/* <HStack > */}
                          <Image
                            src={page?.image}
                            alt=""
                            width="60px"
                            height="60px"
                            borderRadius="50%"
                            fallback={
                              <Flex
                                p={2}
                                bg="#D9D9D9"
                                width="60px"
                                height="60px"
                                justifyContent="center"
                                alignItems="center"
                                borderRadius="50%"
                              >
                                <CompanyIndustryIcon
                                  width="30px"
                                  height="30px"
                                  color="#868585"
                                />
                              </Flex>
                            }
                          />
                          {/* </HStack> */}
                          <Stack pr={0} gap={0} pt={2}>
                            <Heading
                              as="a"
                              href={page?.is_deactivated ? "#" : `/page/private/${page.id}/`}
                              // href={`/c/${page?.slug}`}
                              fontSize={["18px", "20px", "20px","24px"]}
                              color="#2E2E2A"
                              fontWeight="500"
                              maxWidth="100%"
                              overflow="hidden"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                              isTruncated
                            >
                              {page?.name?.length > 19
                                ? page?.name?.slice(0, 19) + '...'
                                : page?.name}
                            </Heading>

                            <Text size="sm">{page?.company_type}</Text>
                          </Stack>
                        </HStack>
                        <Stack height={'100px'} mt={4} mb={4} spacing={2}>
                          {getUserLocation(page) && (
                            <HStack alignItems="flex-start">
                              <Box flexShrink={0}>
                                <JobLocationIcon
                                  color="#9F9F9F"
                                  width={'17'}
                                  height="17"
                                  style={{ marginTop: '2px' }}
                                />
                              </Box>
                              <Text noOfLines={1} title={getUserLocation(page)} fontSize="14px" fontWeight="400" >
                                {/* {getUserLocation(page)?.length > 25
                                  ? getUserLocation(page)?.slice(0, 25) + '...'
                                  : getUserLocation(page)} */}
                                {getUserLocation(page)}
                              </Text>
                            </HStack>
                          )}

                          {page?.main_industry && (
                            <HStack alignItems="flex-start">
                              <Box flexShrink={0}>
                                <CompanyIndustryIconNew
                                  color="#9F9F9F"
                                  width={'17'}
                                  height="17"
                                  style={{ marginTop: '2px' }}
                                />
                              </Box>
                              <Text size="sm">{page?.main_industry}</Text>
                            </HStack>
                          )}
                          {page?.website && (
                            <HStack alignItems="flex-start">
                              <Box flexShrink={0}>
                                <WebsiteIcon
                                  color="#9F9F9F"
                                  width={'17'}
                                  height="17"
                                  style={{ marginTop: '2px' }}
                                />
                              </Box>
                              <Link href={page?.website && !/^https?:\/\//.test(page?.website) ? `http://${page.website}` : page?.website} target="_blank" rel="noopener noreferrer" size="sm">
                                {page?.website?.length > 35
                                  ? page?.website?.slice(0, 35) + '...'
                                  : page?.website}
                              </Link>
                            </HStack>
                          )}
                          {page?.follower_count && (
                            <HStack alignItems="flex-start">
                              <Box flexShrink={0}>
                                <FollowIcon
                                  color="#9F9F9F"
                                  width={'17'}
                                  height="17"
                                  style={{ marginTop: '2px' }}
                                />
                              </Box>
                              <Text size="sm">
                                {page?.follower_count}{' '}
                                {page?.follower_count === 1
                                  ? t('pages.follower') || 'Follower'
                                  : t('pages.followers') || 'Followers'}
                              </Text>
                            </HStack>
                          )}
                        </Stack>
                        <Stack>
                          <HStack>
                            <Link 
                              href={page?.is_deactivated ? "#" : `/page/private/${page.id}/`}
                              pointerEvents={page?.is_deactivated ? "none" : "auto"}
                            >
                              <Button
                                variant="none"
                                ms="auto"
                                sx={{padding: '0 13px'}}
                                fontWeight="normal"
                                borderRadius="50px"
                                color={page?.is_deactivated ? "#A0A0A0" : "#747474"}
                                border="1px solid #00000033"
                                opacity={page?.is_deactivated ? 0.7 : 1}
                                cursor={page?.is_deactivated ? "not-allowed" : "pointer"}
                                _hover={page?.is_deactivated ? {} : { backgroundColor: '#E4E6EB' }}
                                onClick={(e) => page?.is_deactivated && e.preventDefault()}
                              >
                                {t('pages.managePage') || 'Manage Page'}
                              </Button>
                            </Link>

                            {!(page?.access_type==="hiring_admin" || page?.access_type==="content_admin")&&<Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<HorizontalThreeDot />}
                                sx={{
                                  borderRadius: '27px ',
                                  border: '1px solid #00000033',
                                  backgroundColor: 'transparent',
                                  color: 'inherit',
                                  _hover: {
                                    backgroundColor: '#E4E6EB',
                                    color: 'inherit',
                                  },
                                  _focus: {
                                    backgroundColor: 'white',
                                    color: 'inherit',
                                  },
                                }}
                              />

                              <MenuList>
                                <MenuItem
                                  bg="transparent"
                                  color="red"
                                  onClick={() => deletePage(page, 'delete')}
                                  _hover={{ background: "#F5F5F5" }}
                                >
                                  {t('pages.deletePage') || 'Delete Page'}
                                </MenuItem>
                                {page?.is_deactivated && <MenuItem
                                  bg="transparent"
                                  color="green"
                                  onClick={() => deletePage(page, 'activate')}
                                  _hover={{  background: "#F5F5F5" }}
                                >
                                  {t('pages.activatePage') || 'Activate Page'}
                                </MenuItem>}
                                {!page?.is_deactivated && <MenuItem
                                  bg="transparent"
                                  color="red"
                                  onClick={() => deletePage(page, 'deactivate')}
                                  _hover={{  background: "#F5F5F5" }}
                                >
                                  {t('pages.deactivatePage') || 'Deactivate Page'}
                                </MenuItem>}
                              </MenuList>
                            </Menu>}
                          </HStack>
                        </Stack>
                      </Card>
                    );
                  })
              }
            </Grid>
          ) : (
            <Flex justifyContent="center" alignItems="center" h="100%" flexDirection="column">
              <PermissionImg />
              <Text fontWeight="600">{t('settingsPermission.noPagesCreated') || 'You don\'t have any pages created. Please create a page.'}</Text>
            </Flex>
          )}
        </Stack>

        <DeactivatePageModal
          company={company}
          onClose={onClose}
          setModalType={setModalType}
          showModal={modalType === 'deactivate' ? modalType : null}
        />
        <DeactivatePageModal
          company={company}
          onClose={onClose}
          setModalType={setModalType}
          showModal={modalType === 'delete' ? modalType : null}
        />
        {/* {modalType === 'manage' && (
        <ManagePageModal company={company} onClose={onClose} />
      )} */}
      </Box>
    </AuthenticatedProviders>
  );
}
